/**
 * MonthlyView Component
 * Displays panchanga for an entire month in a printable calendar grid
 * Design inspired by the monthly calendar image generator (vedic_calendar/)
 */

import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { ChevronLeft, ChevronRight, Sun, Printer } from 'lucide-react';
import type { MonthlyPanchangaResponse, PanchangaResponse, IntradayWindow } from '../../types/api';
import type { Locale } from '../../types/api';
import { isKrishnaPaksha } from '../../lib/paksha';

/** Extract compact time strings from intraday_windows for monthly cell display */
function extractIntradayTimes(windows?: IntradayWindow[]) {
  if (!windows || windows.length === 0) return { rahu: '', yamaG: '', gulika: '', durmu: '', abhijit: '', amrit: '' };
  const fmt = (w: IntradayWindow) => `${w.start_time}–${w.end_time}`;
  const find = (nameEn: string) => windows.find(w => w.name_en === nameEn);
  const findAll = (nameEn: string) => windows.filter(w => w.name_en === nameEn);

  const rahuW = find('Rahu Kalam');
  const yamaGW = find('Yama Gandam');
  const gulikaW = find('Gulika Kalam');
  const durmuAll = findAll('Durmuhurtham');
  const abhijitW = find('Abhijit Muhurta');
  const amritW = find('Amrit Kala');

  return {
    rahu: rahuW ? fmt(rahuW) : '',
    yamaG: yamaGW ? fmt(yamaGW) : '',
    gulika: gulikaW ? fmt(gulikaW) : '',
    durmu: durmuAll.length > 0 ? durmuAll.map(fmt).join(', ') : '',
    abhijit: abhijitW ? fmt(abhijitW) : '',
    amrit: amritW ? fmt(amritW) : '',
  };
}

interface MonthlyViewProps {
  monthlyData: MonthlyPanchangaResponse;
  onDayClick?: (date: string) => void;
  onMonthChange?: (year: number, month: number) => void;
  locale?: Locale;
}

// ── Moon Phase Icon (same SVG technique as Kaala Chakra) ────────────

const TITHI_PATTERNS: [RegExp, number][] = [
  [/prathamā|प्रथमा|ప్రథమా|ಪ್ರಥಮಾ/i, 1],
  [/dvitīyā|द्वितीया|ద్వితీయా|ದ್ವಿತೀಯಾ/i, 2],
  [/tṛtīyā|तृतीया|తృతీయా|ತೃತೀಯಾ/i, 3],
  [/caturthī|चतुर्थी|చతుర్థీ|ಚತುರ್ಥೀ/i, 4],
  [/pañcamī|पञ्चमी|పంచమీ|ಪಂಚಮೀ/i, 5],
  [/ṣaṣṭhī|षष्ठी|షష్ఠీ|ಷಷ್ಠೀ/i, 6],
  [/saptamī|सप्तमी|సప్తమీ|ಸಪ್ತಮೀ/i, 7],
  [/aṣṭamī|अष्टमी|అష్టమీ|ಅಷ್ಟಮೀ/i, 8],
  [/navamī|नवमी|నవమీ|ನವಮೀ/i, 9],
  [/daśamī|दशमी|దశమీ|ದಶಮೀ/i, 10],
  [/ekādaśī|एकादशी|ఏకాదశీ|ಏಕಾದಶೀ/i, 11],
  [/dvādaśī|द्वादशी|ద్వాదశీ|ದ್ವಾದಶೀ/i, 12],
  [/trayodaśī|त्रयोदशी|త్రయోదశీ|ತ್ರಯೋದಶೀ/i, 13],
  [/caturdaśī|चतुर्दशी|చతుర్దశీ|ಚತುರ್ದಶೀ/i, 14],
  [/pūrṇimā|पूर्णिमा|పూర్ణిమా|ಪೂರ್ಣಿಮಾ/i, 15],
  [/amāvāsyā|अमावास्या|అమావాస్యా|ಅಮಾವಾಸ್ಯಾ/i, 30],
];

const getMoonIllumination = (paksha: string, tithi: string): { illumination: number; isWaxing: boolean } => {
  // NFD-robust — a precomposed /kṛṣṇa/ misses the decomposed "Kṛṣṇa" the API
  // sends, which would render waning days as waxing. See lib/paksha.
  const isKrishna = isKrishnaPaksha(paksha);
  if (/pūrṇimā|purnim|पूर्णिम|పూర్ణిమ|ಪೂರ್ಣಿಮ/i.test(tithi)) return { illumination: 1, isWaxing: false };
  if (/amāvāsyā|amavas|अमावास|అమావాస|ಅಮಾವಾಸ/i.test(tithi)) return { illumination: 0, isWaxing: true };

  let tithiNum = 8;
  for (const [pattern, num] of TITHI_PATTERNS) {
    if (pattern.test(tithi)) { tithiNum = num; break; }
  }
  if (tithiNum === 30) tithiNum = 15;

  // Śukla: tithi 1→15 maps to new moon→full moon (illumination grows)
  // Kṛṣṇa: tithi 1→15 maps to full moon→new moon (illumination shrinks)
  const angle = (tithiNum / 15) * 180;
  const rawIllumination = (1 - Math.cos((angle * Math.PI) / 180)) / 2;
  const illumination = isKrishna ? 1 - rawIllumination : rawIllumination;
  return { illumination, isWaxing: !isKrishna };
};

const MoonPhaseIcon: React.FC<{ paksha: string; tithi: string; size?: number }> = ({ paksha, tithi, size = 14 }) => {
  const { illumination, isWaxing } = getMoonIllumination(paksha, tithi);
  const r = size / 2 - 1;
  const cx = size / 2;
  const cy = size / 2;
  const topY = cy - r;
  const botY = cy + r;

  if (illumination < 0.01) {
    return (
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="inline-block align-middle">
        <circle cx={cx} cy={cy} r={r} fill="#333" stroke="#888" strokeWidth={0.5} />
      </svg>
    );
  }
  if (illumination > 0.99) {
    return (
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="inline-block align-middle">
        <circle cx={cx} cy={cy} r={r} fill="#F5F5DC" stroke="#ccc" strokeWidth={0.5} />
      </svg>
    );
  }

  const innerR = r * Math.abs(2 * illumination - 1);
  const sweep = illumination > 0.5;
  const outerFlag = isWaxing ? 1 : 0;
  const innerFlag = sweep ? (isWaxing ? 1 : 0) : (isWaxing ? 0 : 1);
  const path = [
    `M ${cx} ${topY}`,
    `A ${r} ${r} 0 0 ${outerFlag} ${cx} ${botY}`,
    `A ${innerR} ${r} 0 0 ${innerFlag} ${cx} ${topY}`,
    'Z',
  ].join(' ');

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="inline-block align-middle">
      <circle cx={cx} cy={cy} r={r} fill="#333" stroke="#888" strokeWidth={0.5} />
      <path d={path} fill="#F5F5DC" />
    </svg>
  );
};

// ── Compact Arrow Notation (all locales) ────────────────────────────
// Uses same word mappings as vedic_calendar/calendar_utils.py remove_time_indicators()
// "Prathamā till 10:30, then Dvitīyā afterwards" → "Prathamā 10:30 → Dvitīyā"

// "till" equivalents per locale
const TILL_WORDS = ['till', 'యావత్', 'వరకు', 'तक', 'यावत्', 'ವರೆಗೆ'];
// "afterwards" equivalents per locale
const AFTER_WORDS = ['afterwards', 'తర్వాత', 'बाद में', 'अनन्तरम्', 'ನಂತರ'];
// "then" equivalents per locale
const THEN_WORDS = ['then', 'ఆపై', 'फिर', 'ततः', 'ನಂತರ'];
// "and" equivalents per locale
const AND_WORDS = ['and', 'మరియు', 'और', 'च', 'ಮತ್ತು'];

const compactPanchangaText = (text: string): string => {
  if (!text) return '--';
  const s = text.replace(/\n/g, ' ').trim();

  // Locale-agnostic: extract times and names separately, then reassemble.
  // Handles both English ("name till TIME") and non-English ("name TIME till") word order,
  // plus Devanagari digits (Sanskrit: १७:०९).

  // 1. Extract all time patterns (ASCII + Devanagari digits)
  const timeMatches: string[] = [];
  const timeRegex = /[\d०-९]{1,2}:[\d०-९]{2}/g;
  let tm: RegExpExecArray | null;
  while ((tm = timeRegex.exec(s)) !== null) timeMatches.push(tm[0]);

  // 2. Strip keywords and times to isolate panchanga names
  // Sort keywords longest-first so "बाद में" matches before "में"
  const allKeywords = [...TILL_WORDS, ...AFTER_WORDS, ...THEN_WORDS, ...AND_WORDS]
    .sort((a, b) => b.length - a.length);
  const escaped = allKeywords.map(w => w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  const kwRegex = new RegExp(`(?:${escaped.join('|')})`, 'gi');

  let cleaned = s.replace(/[\d०-९]{1,2}:[\d०-९]{2}/g, ','); // replace times with comma
  cleaned = cleaned.replace(kwRegex, ',');                      // replace keywords with comma
  const names = cleaned.split(/[,]+/).map(n => n.trim()).filter(n => n.length > 0);

  if (names.length === 0) return s;
  if (names.length === 1 && timeMatches.length === 0) return names[0];
  if (names.length === 1 && timeMatches.length >= 1) return `${names[0]} ${timeMatches[0]}`;
  if (names.length === 2 && timeMatches.length >= 1) return `${names[0]} ${timeMatches[0]} → ${names[1]}`;
  if (names.length >= 3 && timeMatches.length >= 2) return `${names[0]} ${timeMatches[0]} → ${names[1]} ${timeMatches[1]} → ${names[2]}`;
  if (names.length >= 2 && timeMatches.length === 0) return names.join(' → ');

  return s;
};

// ── Devanagari digit conversion ─────────────────────────────────────
const DEVANAGARI_DIGITS: Record<string, string> = {
  '०': '0', '१': '1', '२': '2', '३': '3', '४': '4',
  '५': '5', '६': '6', '७': '7', '८': '8', '९': '9',
};
const toArabicDigits = (s: string): string =>
  s.replace(/[०-९]/g, ch => DEVANAGARI_DIGITS[ch] || ch);

const parseDayNumber = (dateStr: string): number => {
  const normalized = toArabicDigits(dateStr);
  return parseInt(normalized.split('-')[2]);
};

// ── Colors & Labels ─────────────────────────────────────────────────

const MONTH_COLORS: string[] = [
  'rgb(139, 10, 26)', 'rgb(75, 0, 130)', 'rgb(135, 206, 235)', 'rgb(239, 67, 29)',
  'rgb(50, 205, 50)', 'rgb(239, 67, 29)', 'rgb(220, 20, 60)', 'rgb(242, 196, 100)',
  'rgb(0, 74, 173)', 'rgb(3, 5, 91)', 'rgb(255, 160, 122)', 'rgb(0, 151, 167)',
];

const WEEKDAY_COLORS: string[] = [
  'rgb(235, 120, 50)', 'rgb(135, 206, 235)', 'rgb(239, 67, 29)', 'rgb(0, 191, 99)',
  'rgb(232, 197, 15)', 'rgb(220, 20, 60)', 'rgb(0, 74, 173)',
];

const CALENDAR_LABELS: Record<string, {
  title: string;
  months: string[];
  weekDays: string[];
  abbrev: { ti: string; n: string; yo: string; ka: string; ra: string; yg: string; gk: string; du: string; ab: string; am: string };
  paksha: { sukla: string; krsna: string };
}> = {
  en: {
    title: 'Pañcāṅgam',
    months: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
    weekDays: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
    abbrev: { ti: 'Ti', n: 'N', yo: 'Yo', ka: 'Ka', ra: 'RK', yg: 'YG', gk: 'GK', du: 'DM', ab: 'AM', am: 'AK' },
    paksha: { sukla: 'Su.', krsna: 'Kr.' },
  },
  sa: {
    title: 'पञ्चाङ्गम्',
    months: ['जनवरी', 'फरवरी', 'मार्च', 'अप्रैल', 'मई', 'जून', 'जुलाई', 'अगस्त', 'सितम्बर', 'अक्टूबर', 'नवम्बर', 'दिसम्बर'],
    weekDays: ['रवि', 'सोम', 'मङ्गल', 'बुध', 'गुरु', 'शुक्र', 'शनि'],
    abbrev: { ti: 'ति', n: 'न', yo: 'यो', ka: 'क', ra: 'राका', yg: 'यग', gk: 'गुका', du: 'दुमु', ab: 'अभि', am: 'अमृ' },
    paksha: { sukla: 'शु.', krsna: 'कृ.' },
  },
  hi: {
    title: 'पञ्चाङ्ग',
    months: ['जनवरी', 'फरवरी', 'मार्च', 'अप्रैल', 'मई', 'जून', 'जुलाई', 'अगस्त', 'सितंबर', 'अक्टूबर', 'नवंबर', 'दिसंबर'],
    weekDays: ['रवि', 'सोम', 'मंगल', 'बुध', 'गुरु', 'शुक्र', 'शनि'],
    abbrev: { ti: 'ति', n: 'न', yo: 'यो', ka: 'क', ra: 'राका', yg: 'यग', gk: 'गुका', du: 'दुमु', ab: 'अभि', am: 'अमृ' },
    paksha: { sukla: 'शु.', krsna: 'कृ.' },
  },
  te: {
    title: 'పంచాంగము',
    months: ['జనవరి', 'ఫిబ్రవరి', 'మార్చి', 'ఏప్రిల్', 'మే', 'జూన్', 'జులై', 'ఆగస్టు', 'సెప్టెంబర్', 'అక్టోబర్', 'నవంబర్', 'డిసెంబర్'],
    weekDays: ['ఆది', 'సోమ', 'మంగళ', 'బుధ', 'గురు', 'శుక్ర', 'శని'],
    abbrev: { ti: 'తి', n: 'న', yo: 'యో', ka: 'క', ra: 'రాకా', yg: 'యగ', gk: 'గుకా', du: 'దుము', ab: 'అభి', am: 'అమృ' },
    paksha: { sukla: 'శు.', krsna: 'కృ.' },
  },
  kn: {
    title: 'ಪಂಚಾಂಗ',
    months: ['ಜನವರಿ', 'ಫೆಬ್ರವರಿ', 'ಮಾರ್ಚ್', 'ಏಪ್ರಿಲ್', 'ಮೇ', 'ಜೂನ್', 'ಜುಲೈ', 'ಆಗಸ್ಟ್', 'ಸೆಪ್ಟೆಂಬರ್', 'ಅಕ್ಟೋಬರ್', 'ನವೆಂಬರ್', 'ಡಿಸೆಂಬರ್'],
    weekDays: ['ಭಾನು', 'ಸೋಮ', 'ಮಂಗಳ', 'ಬುಧ', 'ಗುರು', 'ಶುಕ್ರ', 'ಶನಿ'],
    abbrev: { ti: 'ತಿ', n: 'ನ', yo: 'ಯೋ', ka: 'ಕ', ra: 'ರಾಕಾ', yg: 'ಯಗ', gk: 'ಗುಕಾ', du: 'ದುಮು', ab: 'ಅಭಿ', am: 'ಅಮೃ' },
    paksha: { sukla: 'ಶು.', krsna: 'ಕೃ.' },
  },
};

// ── Helpers ──────────────────────────────────────────────────────────

const getPakshaAbbr = (paksha: string, labels: typeof CALENDAR_LABELS['en']): string => {
  return isKrishnaPaksha(paksha) ? labels.paksha.krsna : labels.paksha.sukla;
};

const parseMasa = (masaString: string) => {
  // Format: "<Amānta label> - <name>, <Purnimānta label> - <name>"
  // Works across all locales by splitting on " - " delimiter
  const parts = masaString.split(',').map(s => s.trim());
  const extractName = (part: string) => {
    const idx = part.lastIndexOf(' - ');
    return idx >= 0 ? part.substring(idx + 3).trim() : null;
  };
  return {
    amaanta: parts[0] ? extractName(parts[0]) : null,
    purnimaanta: parts[1] ? extractName(parts[1]) : null,
  };
};

// Collect all festivals with their day numbers for empty-cell display
const collectFestivals = (days: PanchangaResponse[]): { day: number; name: string }[] => {
  const festivals: { day: number; name: string }[] = [];
  days.forEach(d => {
    if (d.festivals && d.festivals.length > 0) {
      const dayNum = parseDayNumber(d.date);
      d.festivals.forEach(f => festivals.push({ day: dayNum, name: f }));
    }
  });
  return festivals;
};

// ── Print Styles (injected once) ────────────────────────────────────

const PrintStyles: React.FC = () => (
  <style>{`
    @media print {
      /* Hide everything except the calendar */
      body > *:not(#root) { display: none !important; }
      nav, header, footer, .no-print { display: none !important; }

      /* Landscape — fits both A4 (297×210mm) and US Letter (279×216mm) */
      @page { size: landscape; margin: 0.1in; }

      .monthly-calendar-print {
        width: 100% !important;
        max-width: none !important;
        border: none !important;
        box-shadow: none !important;
      }
      .monthly-calendar-print .print-hide { display: none !important; }

      /* Hide Panchangam banner in print — reclaim vertical space */
      .monthly-print-banner { display: none !important; }

      /* Strip card padding — push content to top edge */
      .monthly-calendar-print { margin: 0 !important; padding: 0 !important; }
      .monthly-calendar-print > div { padding: 0px 4px !important; margin: 0 !important; }
      .monthly-calendar-print > div > div { padding: 0px 4px !important; }

      /* Header — compact, minimal vertical space */
      .monthly-calendar-print .pb-3 { padding-bottom: 2px !important; }
      .monthly-calendar-print .pt-6 { padding-top: 2px !important; }
      .monthly-calendar-print .space-y-1\\.5 > * + * { margin-top: 0 !important; }

      /* Grid — 5 rows, cells size to content */
      .monthly-calendar-print .grid { gap: 1px !important; }
      .monthly-calendar-print .monthly-print-cell { min-height: auto !important; }

      /* Header: month title + metadata */
      .monthly-print-date { font-size: 15px !important; line-height: 1.1 !important; }
      .monthly-print-title { font-size: 32px !important; line-height: 1 !important; }
      .monthly-print-meta { font-size: 10px !important; line-height: 1.15 !important; }
      .monthly-print-weekday { font-size: 10px !important; padding: 0 !important; }
      .monthly-calendar-print .grid.mb-2 { margin-bottom: 0 !important; }

      /* Day cells — sized to fit A4 landscape (shorter width than Letter) */
      .monthly-print-cell { padding: 1px 2px !important; }
      .monthly-print-cell > div { margin-bottom: 0 !important; }
      .monthly-print-panchanga { font-size: 8.5px !important; line-height: 1.15 !important; }
      .monthly-print-panchanga > div { margin-top: 0 !important; }
      .monthly-print-sunrise { font-size: 7px !important; }
      .monthly-print-muhurtha { font-size: 7px !important; line-height: 1.1 !important; margin-top: 1px !important; padding-top: 1px !important; }
      .monthly-print-muhurtha .border-t { border-top-width: 0.5px !important; }

      /* Festival cells */
      .monthly-print-festival { font-size: 8px !important; line-height: 1.15 !important; }

      /* Footer */
      .monthly-print-footer { font-size: 8px !important; padding: 1px 0 !important; }

      /* Ensure colors print */
      .monthly-calendar-print * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      .monthly-calendar-print [style*="color"] { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      .monthly-calendar-print svg { print-color-adjust: exact; -webkit-print-color-adjust: exact; }

      /* Moon phase icon — smaller in print */
      .monthly-print-cell svg { width: 10px !important; height: 10px !important; }
    }
  `}</style>
);

// ── Component ───────────────────────────────────────────────────────

export const MonthlyView: React.FC<MonthlyViewProps> = ({ monthlyData, onDayClick, onMonthChange, locale = 'en' }) => {
  const labels = CALENDAR_LABELS[locale] || CALENDAR_LABELS.en;
  const monthColor = MONTH_COLORS[monthlyData.month - 1];

  const today = new Date();
  const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

  const firstDay = new Date(monthlyData.year, monthlyData.month - 1, 1);
  const firstDayOfWeek = firstDay.getDay();

  // Build calendar grid — wrap 6th row into leading empty cells of row 1
  // (matches Python monthly.py behavior: always 5 rows)
  const calendarDays: (typeof monthlyData.days[0] | null)[] = [];
  for (let i = 0; i < firstDayOfWeek; i++) calendarDays.push(null);
  monthlyData.days.forEach(day => calendarDays.push(day));
  while (calendarDays.length % 7 !== 0) calendarDays.push(null);
  // If 6 rows, move last row's days into leading nulls of row 1
  if (calendarDays.length > 35) {
    for (let i = 35; i < calendarDays.length; i++) {
      if (calendarDays[i] !== null) {
        // Find first null in row 1 (indices 0..6) to place this day
        const nullIdx = calendarDays.slice(0, 7).findIndex(d => d === null);
        if (nullIdx >= 0) calendarDays[nullIdx] = calendarDays[i];
      }
    }
    calendarDays.length = 35; // trim to 5 rows
  }

  // Vedic context — detect transitions across the month
  const firstDayData = monthlyData.days[0];
  const lastDayData = monthlyData.days[monthlyData.days.length - 1];

  const detectTransition = (field: keyof PanchangaResponse): string => {
    const first = firstDayData?.[field] as string | undefined;
    const last = lastDayData?.[field] as string | undefined;
    if (!first) return '';
    if (!last || first === last) return first;
    return `${first} → ${last}`;
  };

  // Masa: extract Amānta and Pūrnimānta names, show transitions
  const firstMasa = firstDayData?.masa ? parseMasa(firstDayData.masa) : null;
  const lastMasa = lastDayData?.masa ? parseMasa(lastDayData.masa) : null;
  const amaantaDisplay = firstMasa?.amaanta && lastMasa?.amaanta && firstMasa.amaanta !== lastMasa.amaanta
    ? `${firstMasa.amaanta} → ${lastMasa.amaanta}`
    : firstMasa?.amaanta || '';
  const purnimaantaDisplay = firstMasa?.purnimaanta && lastMasa?.purnimaanta && firstMasa.purnimaanta !== lastMasa.purnimaanta
    ? `${firstMasa.purnimaanta} → ${lastMasa.purnimaanta}`
    : firstMasa?.purnimaanta || '';

  // Samvatsara & Rtu transitions
  const samvatsaraDisplay = detectTransition('samvatsara');
  const rtuDisplay = detectTransition('rithu');

  // Collect all festivals for empty-cell display
  const allFestivals = collectFestivals(monthlyData.days);

  // Build a set of days that have festivals (for date highlighting)
  const festivalDaySet = new Set<number>();
  monthlyData.days.forEach(d => {
    if (d.festivals && d.festivals.length > 0) {
      festivalDaySet.add(parseDayNumber(d.date));
    }
  });

  // Find ALL empty cell indices (both start and end of grid)
  // But keep at least 1 empty cell before festivals in the first row (before day 1)
  const allEmptyIndices = calendarDays
    .map((d, i) => (d === null ? i : -1))
    .filter(i => i >= 0);

  // Reserve 1 empty cell gap before day 1 if there are leading empties
  const leadingEmpties = allEmptyIndices.filter(i => i < firstDayOfWeek);
  const trailingEmpties = allEmptyIndices.filter(i => i >= firstDayOfWeek);
  // Use leading empties (skip first one as gap) + all trailing empties
  const festivalCellIndices = [
    ...leadingEmpties.slice(0, -1),  // all but last leading empty (last stays as gap before day 1)
    ...trailingEmpties,
  ];

  // If not enough empty cells, add an extra row
  const maxPerCell = 6;
  const cellsNeeded = Math.ceil(allFestivals.length / maxPerCell);
  if (cellsNeeded > festivalCellIndices.length) {
    const extraNeeded = (cellsNeeded - festivalCellIndices.length);
    const extraRows = Math.ceil(extraNeeded / 7);
    for (let r = 0; r < extraRows; r++) {
      for (let i = 0; i < 7; i++) {
        const newIdx = calendarDays.length;
        calendarDays.push(null);
        festivalCellIndices.push(newIdx);
      }
    }
  }

  // Split festivals into chunks for empty cells
  const festivalChunks: { day: number; name: string }[][] = [];
  for (let i = 0; i < allFestivals.length; i += maxPerCell) {
    festivalChunks.push(allFestivals.slice(i, i + maxPerCell));
  }

  // Navigation
  const handlePrevMonth = () => {
    if (!onMonthChange) return;
    onMonthChange(
      monthlyData.month === 1 ? monthlyData.year - 1 : monthlyData.year,
      monthlyData.month === 1 ? 12 : monthlyData.month - 1
    );
  };
  const handleNextMonth = () => {
    if (!onMonthChange) return;
    onMonthChange(
      monthlyData.month === 12 ? monthlyData.year + 1 : monthlyData.year,
      monthlyData.month === 12 ? 1 : monthlyData.month + 1
    );
  };

  return (
    <>
      <PrintStyles />
      <Card className="w-full monthly-calendar-print">
        {/* Panchangam banner — hidden on screen, shown in print */}
        <div className="hidden monthly-print-banner" style={{ color: monthColor }}>
          {labels.title}
        </div>
        <CardHeader className="pb-3">
          <CardTitle>
            <div className="flex items-center gap-1">
              {/* Left: Samvatsara & Ayana */}
              {firstDayData && (
                <div className="text-[13px] leading-snug text-left min-w-0 shrink monthly-print-meta">
                  {samvatsaraDisplay && (
                    <div><span className="font-semibold text-red-800 dark:text-red-400">Samvatsara:</span> <span className="text-foreground">{samvatsaraDisplay}</span></div>
                  )}
                  {firstDayData.ayana && (
                    <div><span className="font-semibold text-red-800 dark:text-red-400">Ayana:</span> <span className="text-foreground">{firstDayData.ayana}</span></div>
                  )}
                </div>
              )}

              {/* Center: nav + month title */}
              <div className="flex-1 flex items-center justify-center gap-1">
                {onMonthChange && (
                  <button onClick={handlePrevMonth} className="p-1 rounded hover:bg-accent transition-colors print-hide" aria-label="Previous month">
                    <ChevronLeft className="h-5 w-5" />
                  </button>
                )}
                <span className="text-4xl font-bold whitespace-nowrap monthly-print-title" style={{ color: monthColor }}>
                  {labels.months[monthlyData.month - 1]} - {monthlyData.year}
                </span>
                {onMonthChange && (
                  <button onClick={handleNextMonth} className="p-1 rounded hover:bg-accent transition-colors print-hide" aria-label="Next month">
                    <ChevronRight className="h-5 w-5" />
                  </button>
                )}
                <button onClick={() => window.print()} className="p-1 rounded hover:bg-accent transition-colors print-hide" aria-label="Print calendar" title="Print calendar">
                  <Printer className="h-5 w-5 text-muted-foreground" />
                </button>
              </div>

              {/* Right: Māsa & Ṛtu */}
              {firstDayData && (
                <div className="text-[13px] leading-snug text-left min-w-0 shrink mr-16 monthly-print-meta">
                  {amaantaDisplay && (
                    <div><span className="font-semibold text-red-800 dark:text-red-400">Amānta:</span> <span className="text-foreground">{amaantaDisplay}</span></div>
                  )}
                  {purnimaantaDisplay && (
                    <div><span className="font-semibold text-red-800 dark:text-red-400">Pūrnimānta:</span> <span className="text-foreground">{purnimaantaDisplay}</span></div>
                  )}
                  {rtuDisplay && (
                    <div><span className="font-semibold text-red-800 dark:text-red-400">Ṛtu:</span> <span className="text-foreground">{rtuDisplay}</span></div>
                  )}
                </div>
              )}
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {/* Desktop: 7-column grid */}
          <div className="hidden sm:block" role="grid" aria-label={`${labels.months[monthlyData.month - 1]} ${monthlyData.year} calendar`}>
            <div className="grid grid-cols-7 gap-1 mb-2" role="row">
              {labels.weekDays.map((day, idx) => (
                <div key={day} role="columnheader" className="text-center text-base font-bold py-1.5 rounded monthly-print-weekday" style={{ color: WEEKDAY_COLORS[idx] }}>
                  {day}
                </div>
              ))}
            </div>
            <div className="grid grid-cols-7 gap-1">
              {calendarDays.map((day, index) => {
                // Empty cell — show festival overflow if in festivalCellIndices
                if (!day) {
                  const chunkIdx = festivalCellIndices.indexOf(index);
                  const chunk = chunkIdx >= 0 && chunkIdx < festivalChunks.length ? festivalChunks[chunkIdx] : null;

                  return (
                    <div key={`empty-${index}`} className="min-h-[140px] border border-border/30 rounded bg-muted/20 p-1.5 monthly-print-cell">
                      {chunk && (
                        <div className="text-[11px] leading-tight monthly-print-festival" style={{ color: 'rgb(110, 46, 237)' }}>
                          <div className="font-bold mb-0.5 text-[11px] border-b border-purple-200 pb-0.5">Festivals</div>
                          {chunk.map((f, i) => (
                            <div key={i}><span className="font-bold">{f.day}*:</span> {f.name}</div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                }

                const dayNumber = parseDayNumber(day.date);
                const hasDayFestivals = festivalDaySet.has(dayNumber);
                const normalizedDate = toArabicDigits(day.date);
                const isTodayNorm = normalizedDate === todayStr;

                const { rahu, yamaG, gulika, durmu, abhijit, amrit } = extractIntradayTimes(day.intraday_windows ?? undefined);

                return (
                  <div
                    key={day.date}
                    onClick={() => onDayClick?.(day.date)}
                    className={`min-h-[140px] p-1.5 border border-border/50 rounded cursor-pointer hover:bg-accent/50 transition-colors monthly-print-cell ${isTodayNorm ? 'ring-2 ring-amber-500 bg-amber-50/30 dark:bg-amber-950/20' : ''}`}
                  >
                    {/* Row 1: ☀ sunrise-sunset | DATE (large, black) | 🌙 moon phase */}
                    <div className="flex items-center mb-0.5">
                      {day.sunrise && day.sunset && (
                        <span className="flex items-center gap-0.5 text-[9px] whitespace-nowrap monthly-print-sunrise" style={{ color: 'rgb(207, 96, 36)' }}>
                          <Sun className="h-3 w-3 flex-shrink-0" />
                          {day.sunrise}-{day.sunset}
                        </span>
                      )}
                      <span className="flex-1 text-center text-2xl font-extrabold text-gray-900 dark:text-gray-100 monthly-print-date">
                        {isTodayNorm ? (
                          <span className="text-amber-600 dark:text-amber-400">{dayNumber}</span>
                        ) : hasDayFestivals ? (
                          <span style={{ color: 'rgb(110, 46, 237)' }}>{dayNumber}*</span>
                        ) : (
                          dayNumber
                        )}
                      </span>
                      {day.paksha && <MoonPhaseIcon paksha={day.paksha} tithi={day.tithi} size={14} />}
                    </div>

                    {/* Panchanga elements — compact arrow notation, 11px */}
                    <div className="text-[11px] leading-tight space-y-px font-medium text-gray-900 dark:text-gray-100 monthly-print-panchanga">
                      <div><span className="font-bold text-purple-700 dark:text-purple-400">{labels.abbrev.ti}:</span> {day.paksha && <span className="text-gray-500 dark:text-gray-400">{getPakshaAbbr(day.paksha, labels)}</span>} {compactPanchangaText(day.tithi)}</div>
                      <div><span className="font-bold text-blue-700 dark:text-blue-400">{labels.abbrev.n}:</span> {compactPanchangaText(day.nakshatra)}</div>
                      <div><span className="font-bold text-green-700 dark:text-green-400">{labels.abbrev.yo}:</span> {compactPanchangaText(day.yoga)}</div>
                      <div><span className="font-bold text-brand dark:text-brand-dark">{labels.abbrev.ka}:</span> {compactPanchangaText(day.karana)}</div>
                    </div>

                    {/* Kāla Viśeṣa — inauspicious/auspicious paired on single lines */}
                    {(rahu || yamaG || gulika || durmu || abhijit || amrit) && (
                      <div className="text-[9px] leading-tight mt-1 pt-1 border-t border-border text-gray-700 dark:text-gray-300 monthly-print-muhurtha">
                        {(rahu || yamaG || gulika) && (
                          <div>
                            {rahu && <><span className="font-bold text-red-700 dark:text-red-400">{labels.abbrev.ra}:</span> {rahu}</>}
                            {rahu && yamaG && ' | '}
                            {yamaG && <><span className="font-bold text-red-700 dark:text-red-400">{labels.abbrev.yg}:</span> {yamaG}</>}
                            {(rahu || yamaG) && gulika && ' | '}
                            {gulika && <><span className="font-bold text-red-700 dark:text-red-400">{labels.abbrev.gk}:</span> {gulika}</>}
                          </div>
                        )}
                        {durmu && (
                          <div>
                            <span className="font-bold text-red-700 dark:text-red-400">{labels.abbrev.du}:</span> {durmu}
                          </div>
                        )}
                        {(abhijit || amrit) && (
                          <div>
                            {abhijit && <><span className="font-bold text-emerald-700 dark:text-emerald-400">{labels.abbrev.ab}:</span> {abhijit}</>}
                            {abhijit && amrit && ' | '}
                            {amrit && <><span className="font-bold text-emerald-700 dark:text-emerald-400">{labels.abbrev.am}:</span> {amrit}</>}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Mobile: compact list view */}
          <div className="sm:hidden space-y-2">
            {monthlyData.days.map((day) => {
              const dayNumber = parseDayNumber(day.date);
              const hasDayFestivals = festivalDaySet.has(dayNumber);
              const normalizedDate = toArabicDigits(day.date);
              const isTodayNorm = normalizedDate === todayStr;
              const dayOfWeek = new Date(normalizedDate + 'T00:00:00').getDay();
              const dayOfWeekLabel = labels.weekDays[dayOfWeek];
              const { rahu, yamaG, gulika, durmu, abhijit, amrit } = extractIntradayTimes(day.intraday_windows ?? undefined);

              return (
                <div
                  key={day.date}
                  onClick={() => onDayClick?.(day.date)}
                  className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer hover:bg-accent transition-colors ${isTodayNorm ? 'ring-2 ring-amber-500 border-amber-400 bg-amber-50/50 dark:bg-amber-950/20' : ''}`}
                >
                  <div className="text-center min-w-[52px]">
                    <div className="flex items-center justify-center gap-1">
                      <Sun className="h-3 w-3 flex-shrink-0" style={{ color: 'rgb(207, 96, 36)' }} />
                      <span className="text-2xl font-extrabold text-gray-900 dark:text-gray-100">
                        {isTodayNorm ? (
                          <span className="text-amber-600 dark:text-amber-400">{dayNumber}</span>
                        ) : hasDayFestivals ? (
                          <span style={{ color: 'rgb(110, 46, 237)' }}>{dayNumber}*</span>
                        ) : (
                          dayNumber
                        )}
                      </span>
                      {day.paksha && <MoonPhaseIcon paksha={day.paksha} tithi={day.tithi} size={14} />}
                    </div>
                    <div className="text-xs font-medium" style={{ color: WEEKDAY_COLORS[dayOfWeek] }}>{dayOfWeekLabel}</div>
                    {day.sunrise && day.sunset && (
                      <div className="text-[10px]" style={{ color: 'rgb(207, 96, 36)' }}>{day.sunrise}-{day.sunset}</div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0 text-sm space-y-0.5 font-medium text-gray-900 dark:text-gray-100">
                    <div><span className="font-bold text-purple-700 dark:text-purple-400">{labels.abbrev.ti}:</span> {day.paksha && <span className="text-gray-500 dark:text-gray-400">{getPakshaAbbr(day.paksha, labels)}</span>} {compactPanchangaText(day.tithi)}</div>
                    <div><span className="font-bold text-blue-700 dark:text-blue-400">{labels.abbrev.n}:</span> {compactPanchangaText(day.nakshatra)}</div>
                    <div><span className="font-bold text-green-700 dark:text-green-400">{labels.abbrev.yo}:</span> {compactPanchangaText(day.yoga)}</div>
                    <div><span className="font-bold text-brand dark:text-brand-dark">{labels.abbrev.ka}:</span> {compactPanchangaText(day.karana)}</div>
                    {(rahu || yamaG || gulika || durmu || abhijit || amrit) && (
                      <div className="mt-1 pt-1 border-t border-border space-y-0.5">
                        {(rahu || yamaG || gulika) && (
                          <div className="text-xs text-gray-700 dark:text-gray-300">
                            {rahu && <><span className="font-bold text-red-700 dark:text-red-400">{labels.abbrev.ra}:</span> {rahu}</>}
                            {rahu && yamaG && ' | '}
                            {yamaG && <><span className="font-bold text-red-700 dark:text-red-400">{labels.abbrev.yg}:</span> {yamaG}</>}
                            {(rahu || yamaG) && gulika && ' | '}
                            {gulika && <><span className="font-bold text-red-700 dark:text-red-400">{labels.abbrev.gk}:</span> {gulika}</>}
                          </div>
                        )}
                        {durmu && (
                          <div className="text-xs text-gray-700 dark:text-gray-300">
                            <span className="font-bold text-red-700 dark:text-red-400">{labels.abbrev.du}:</span> {durmu}
                          </div>
                        )}
                        {(abhijit || amrit) && (
                          <div className="text-xs text-gray-700 dark:text-gray-300">
                            {abhijit && <><span className="font-bold text-emerald-700 dark:text-emerald-400">{labels.abbrev.ab}:</span> {abhijit}</>}
                            {abhijit && amrit && ' | '}
                            {amrit && <><span className="font-bold text-emerald-700 dark:text-emerald-400">{labels.abbrev.am}:</span> {amrit}</>}
                          </div>
                        )}
                      </div>
                    )}
                    {hasDayFestivals && (
                      <div className="text-xs font-bold mt-0.5" style={{ color: 'rgb(110, 46, 237)' }}>
                        {day.festivals!.join(', ')}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
        <div className="text-center text-[10px] text-muted-foreground py-1 monthly-print-footer">
          © 2026 www.kaalagyana.com
        </div>
      </Card>
    </>
  );
};

export default MonthlyView;
